import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import LeadModal from '../components/marketing/LeadModal';
import AgendamentoLeadModal from '../components/marketing/AgendamentoLeadModal';
import MarketingKanbanColumn from '../components/marketing/MarketingKanbanColumn';
import LandPageServicosTab from '../components/marketing/LandPageServicosTab';
import CuponsTab from '../components/marketing/CuponsTab';
import WhatsAppChat from '../components/marketing/WhatsAppChat';
import PipelinesConfig from '../components/marketing/PipelinesConfig';
import '../styles/marketing.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEntidadeContext } from '../EntidadeContext';
import axios from 'axios';
import { useAlertModal } from '../AlertModalContext';
import { useAuthContext } from '../context/AuthContext';
import { useUserContext } from '../UserContext';

const MarketingPage = () => {
    const { showAlertModal } = useAlertModal();
    const { loggedInUser: currentUser } = useAuthContext();
    const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig } = useUserContext();

    const [leads, setLeads] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [pipelines, setPipelines] = useState([]);
    const [pipelineAtiva, setPipelineAtiva] = useState(null);
    const [isLoadingPipeline, setIsLoadingPipeline] = useState(true);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [isSavingLead, setIsSavingLead] = useState(false);
    const [showAgendamentoModal, setShowAgendamentoModal] = useState(false);
    const [pacienteParaAgendar, setPacienteParaAgendar] = useState(null);
    const [showPipelinesConfig, setShowPipelinesConfig] = useState(false);
    const [whatsAppLead, setWhatsAppLead] = useState(null);
    const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
    const [filtroResponsavel, setFiltroResponsavel] = useState('');
    const [marketingProfileIds, setMarketingProfileIds] = useState({ standard: [], custom: [] });
    const [activeTab, setActiveTab] = useState('');
    const [servicos, setServicos] = useState([]);
    const [isLoadingServicos, setIsLoadingServicos] = useState(false);

    const { currentEntidade, supportData, isLoadingSupportData, agendaConfig, isLoadingAgendaConfig } = useEntidadeContext();

    const permittedTabs = useMemo(() => {
        return getPermittedInternalTabs('marketing', currentUserRole);
    }, [getPermittedInternalTabs, currentUserRole, isLoadingAccessConfig]);

    useEffect(() => {
        if (permittedTabs.length > 0 && !isLoadingAccessConfig) {
            if (!activeTab || !permittedTabs.some(tab => tab.key === activeTab)) {
                setActiveTab(permittedTabs[0].key);
            }
        }
    }, [permittedTabs, activeTab, isLoadingAccessConfig]);

    const fetchPipelines = useCallback(async () => {
        if (!currentEntidade) return;
        setIsLoadingPipeline(true);
        try {
            const response = await axios.get('/api/pipelines');
            const lista = response.data.pipelines || [];
            setPipelines(lista);
            if (!pipelineAtiva) {
                const padrao = lista.find(p => p.is_default) || lista[0];
                setPipelineAtiva(padrao || null);
            } else {
                const atualizada = lista.find(p => p.id === pipelineAtiva.id);
                if (atualizada) setPipelineAtiva(atualizada);
            }
        } catch (err) {
            console.error('Erro ao carregar pipelines:', err);
        } finally {
            setIsLoadingPipeline(false);
        }
    }, [currentEntidade]);

    useEffect(() => {
        if (activeTab === 'mk_funil_leads' && currentEntidade) fetchPipelines();
    }, [activeTab, currentEntidade, fetchPipelines]);

    useEffect(() => {
        const fetchLeads = async () => {
            if (!currentEntidade) return;
            setIsLoadingData(true);
            setError(null);
            try {
                const response = await axios.get('/api/leads', { params: { entidade_id: currentEntidade.id } });
                setLeads(response.data.leads || []);
                setMarketingProfileIds(response.data.marketing_profile_ids || { standard: [], custom: [] });
            } catch {
                setError('Não foi possível carregar os leads.');
            } finally {
                setIsLoadingData(false);
            }
        };

        const fetchServicos = async () => {
            if (activeTab === 'mk_lp_servicos' && currentEntidade && servicos.length === 0) {
                setIsLoadingServicos(true);
                try {
                    const response = await axios.get(`/api/landpageservicos?entidade_id=${currentEntidade.id}`);
                    setServicos(response.data.servicos);
                } catch { console.error('Erro ao buscar serviços'); }
                finally { setIsLoadingServicos(false); }
            }
        };

        if (activeTab === 'mk_funil_leads') fetchLeads();
        else if (activeTab === 'mk_lp_servicos') fetchServicos();
    }, [activeTab, currentEntidade, isLoadingSupportData, isLoadingAgendaConfig]);

    useEffect(() => {
        if (activeTab !== 'mk_funil_leads' || !currentEntidade) return;
        const interval = setInterval(async () => {
            try {
                const response = await axios.get('/api/leads', { params: { entidade_id: currentEntidade.id } });
                setLeads(response.data.leads || []);
                if (showWhatsAppChat && whatsAppLead) {
                    const leadAtualizado = (response.data.leads || []).find(l => l.id === whatsAppLead.id);
                    if (leadAtualizado) setWhatsAppLead(leadAtualizado);
                }
            } catch { console.error('Polling: Erro ao atualizar leads.'); }
        }, 30000);
        return () => clearInterval(interval);
    }, [activeTab, currentEntidade, showWhatsAppChat, whatsAppLead]);

    const handleCardClick = (lead) => { setSelectedLead(lead); setShowLeadModal(true); };
    const handleShowCreateModal = () => { setSelectedLead(null); setShowLeadModal(true); };

    const handleWhatsAppClick = (lead) => {
        if (showWhatsAppChat && whatsAppLead?.id === lead.id) {
            setShowWhatsAppChat(false); setWhatsAppLead(null); return;
        }
        setWhatsAppLead(lead); setShowWhatsAppChat(true);
    };

    const handleSaveLead = async (leadData) => {
        setIsSavingLead(true);
        try {
            if (leadData.id) {
                const response = await axios.put(`/api/leads/${leadData.id}`, leadData);
                if (response.data?.lead) setLeads(prev => prev.map(l => l.id === leadData.id ? response.data.lead : l));
            } else {
                const response = await axios.post('/api/leads', {
                    ...leadData,
                    entidade_id: currentEntidade.id,
                    pipeline_id: pipelineAtiva?.id,
                    estagio_id: pipelineAtiva?.estagios?.find(e => e.tipo === 'entrada')?.id,
                    status: 'novo',
                });
                if (response.data?.lead) setLeads(prev => [response.data.lead, ...prev]);
            }
            setShowLeadModal(false);
        } catch (error) {
            showAlertModal('Erro ao Salvar', error.response?.data?.message || 'Não foi possível salvar o lead.', 'danger');
        } finally {
            setIsSavingLead(false);
        }
    };

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        const estagioDestino = pipelineAtiva?.estagios?.find(e => String(e.id) === String(destination.droppableId));
        if (estagioDestino?.tipo === 'convertido') {
            showAlertModal('Ação Bloqueada', 'Um lead só pode ser movido para "Convertido" através da criação de um agendamento.', 'warning');
            return;
        }
        const leadId = parseInt(draggableId);
        const originalLeads = [...leads];
        setLeads(prev => prev.map(lead => lead.id === leadId ? { ...lead, estagio_id: estagioDestino?.id } : lead));
        axios.patch(`/api/leads/${leadId}/status`, { estagio_id: estagioDestino?.id })
            .catch(() => { setLeads(originalLeads); showAlertModal('Erro', 'Não foi possível mover o lead.', 'danger'); });
    };

    const filteredLeads = useMemo(() => {
        if (isLoadingData || !pipelineAtiva) return [];
        let filtered = leads.filter(l => l.pipeline_id === pipelineAtiva.id);
        if (filtroResponsavel) filtered = filtered.filter(l => String(l.responsavel_id) === String(filtroResponsavel));
        return filtered;
    }, [leads, filtroResponsavel, isLoadingData, pipelineAtiva]);

    const colaboradoresMarketing = useMemo(() => {
        if (!supportData?.allFuncionarios || (!marketingProfileIds.standard.length && !marketingProfileIds.custom.length)) return [];
        const standardIds = new Set(marketingProfileIds.standard);
        const customIds = new Set(marketingProfileIds.custom);
        return supportData.allFuncionarios.filter(f =>
            (f.standard_role_id && standardIds.has(f.standard_role_id)) ||
            (f.entidade_perfil_id && customIds.has(f.entidade_perfil_id))
        );
    }, [supportData.allFuncionarios, marketingProfileIds]);

    // ── Tab labels modernos ────────────────────────────────────────────────
    const tabIcons = {
        mk_funil_leads: 'project-diagram',
        mk_lp_servicos: 'globe',
        GestaoCupons: 'tag',
    };

    return (
        <>
            {/* ── Wrapper principal ── */}
            <div style={{
                backgroundColor: '#f8fafc',
                minHeight: '100vh',
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
            }}>

                {/* ── Header moderno ── */}
                <div style={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '64px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                    {/* Título */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon="project-diagram" style={{ color: '#fff', fontSize: '16px' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                                Marketing
                            </h1>
                            {currentEntidade && (
                                <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af' }}>
                                    {currentEntidade.nome}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Ações do header — só no funil */}
                    {activeTab === 'mk_funil_leads' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                            {/* Seletor de Pipeline */}
                            {pipelines.length > 1 && (
                                <div style={{ position: 'relative' }}>
                                    <select
                                        value={pipelineAtiva?.id || ''}
                                        onChange={(e) => {
                                            const p = pipelines.find(p => p.id === parseInt(e.target.value));
                                            setPipelineAtiva(p || null);
                                        }}
                                        style={{
                                            padding: '6px 32px 6px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            color: '#374151',
                                            backgroundColor: '#fff',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            appearance: 'none',
                                            minWidth: '160px',
                                        }}
                                    >
                                        {pipelines.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.is_default ? '★ ' : ''}{p.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <FontAwesomeIcon icon="chevron-down" style={{
                                        position: 'absolute', right: '10px', top: '50%',
                                        transform: 'translateY(-50%)', fontSize: '10px', color: '#9ca3af',
                                        pointerEvents: 'none',
                                    }} />
                                </div>
                            )}

                            {/* Filtro Responsável */}
                            <div style={{ position: 'relative' }}>
                                <FontAwesomeIcon icon="user" style={{
                                    position: 'absolute', left: '10px', top: '50%',
                                    transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af',
                                    pointerEvents: 'none',
                                }} />
                                <select
                                    value={filtroResponsavel}
                                    onChange={(e) => setFiltroResponsavel(e.target.value)}
                                    style={{
                                        padding: '6px 32px 6px 30px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: filtroResponsavel ? '#374151' : '#9ca3af',
                                        backgroundColor: '#fff',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        appearance: 'none',
                                        minWidth: '180px',
                                    }}
                                >
                                    <option value="">Todos os responsáveis</option>
                                    {colaboradoresMarketing.map(c => (
                                        <option key={c.id} value={c.id}>{c.nome}</option>
                                    ))}
                                </select>
                                <FontAwesomeIcon icon="chevron-down" style={{
                                    position: 'absolute', right: '10px', top: '50%',
                                    transform: 'translateY(-50%)', fontSize: '10px', color: '#9ca3af',
                                    pointerEvents: 'none',
                                }} />
                            </div>

                            {/* Divider */}
                            <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb' }} />

                            {/* Botão Engrenagem */}
                            <button
                                onClick={() => setShowPipelinesConfig(true)}
                                title="Configurar Pipelines"
                                style={{
                                    width: '36px', height: '36px', borderRadius: '8px',
                                    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
                                    cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    color: '#6b7280', fontSize: '14px', transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#6b7280'; }}
                            >
                                <FontAwesomeIcon icon="cog" />
                            </button>

                            {/* Botão Novo Lead */}
                            <button
                                onClick={handleShowCreateModal}
                                style={{
                                    padding: '8px 16px',
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    color: '#fff', border: 'none', borderRadius: '8px',
                                    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    boxShadow: '0 1px 3px rgba(59,130,246,0.4)',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <FontAwesomeIcon icon="plus" />
                                Novo Lead
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Navegação por abas ── */}
                <div style={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '0 24px',
                    display: 'flex',
                    gap: '4px',
                }}>
                    {permittedTabs.map(tabInfo => (
                        <button
                            key={tabInfo.key}
                            onClick={() => setActiveTab(tabInfo.key)}
                            style={{
                                padding: '12px 16px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: activeTab === tabInfo.key ? '600' : '400',
                                color: activeTab === tabInfo.key ? '#3b82f6' : '#6b7280',
                                borderBottom: activeTab === tabInfo.key ? '2px solid #3b82f6' : '2px solid transparent',
                                marginBottom: '-1px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={e => { if (activeTab !== tabInfo.key) e.currentTarget.style.color = '#374151'; }}
                            onMouseLeave={e => { if (activeTab !== tabInfo.key) e.currentTarget.style.color = '#6b7280'; }}
                        >
                            <FontAwesomeIcon
                                icon={tabIcons[tabInfo.key] || 'circle'}
                                style={{ fontSize: '12px' }}
                            />
                            {tabInfo.title}
                        </button>
                    ))}
                </div>

                {/* ── Conteúdo das abas ── */}
                <div style={{ padding: '0' }}>

                    {/* ABA: Cupons */}
                    {activeTab === 'GestaoCupons' && (
                        <div style={{ padding: '24px' }}>
                            <CuponsTab currentUserRole={currentUserRole} />
                        </div>
                    )}

                    {/* ABA: Landing Page Serviços */}
                    {activeTab === 'mk_lp_servicos' && (
                        <div style={{ padding: '24px' }}>
                            <LandPageServicosTab initialServicos={servicos} isLoading={isLoadingServicos} />
                        </div>
                    )}

                    {/* ABA: Funil de Leads */}
                    {activeTab === 'mk_funil_leads' && (
                        <>
                            {isLoadingData || isLoadingPipeline || isLoadingSupportData || isLoadingAgendaConfig ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px', gap: '12px' }}>
                                    <Spinner animation="border" variant="primary" />
                                    <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Carregando funil...</p>
                                </div>
                            ) : error ? (
                                <div style={{ padding: '24px' }}>
                                    <Alert variant="danger">{error}</Alert>
                                </div>
                            ) : !pipelineAtiva ? (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    justifyContent: 'center', padding: '80px', gap: '16px',
                                }}>
                                    <div style={{
                                        width: '72px', height: '72px', borderRadius: '16px',
                                        backgroundColor: '#f3f4f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <FontAwesomeIcon icon="project-diagram" style={{ fontSize: '32px', color: '#d1d5db' }} />
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                                            Nenhuma pipeline configurada
                                        </h3>
                                        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#9ca3af' }}>
                                            Crie uma pipeline para começar a organizar seus leads
                                        </p>
                                        <button
                                            onClick={() => setShowPipelinesConfig(true)}
                                            style={{
                                                padding: '10px 20px',
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                color: '#fff', border: 'none', borderRadius: '8px',
                                                cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            }}
                                        >
                                            <FontAwesomeIcon icon="cog" />
                                            Configurar Pipeline
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Sub-header da pipeline */}
                                    <div style={{
                                        padding: '12px 24px',
                                        backgroundColor: '#fff',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        <div style={{
                                            width: '10px', height: '10px', borderRadius: '50%',
                                            backgroundColor: pipelineAtiva.cor, flexShrink: 0,
                                        }} />
                                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                            {pipelineAtiva.nome}
                                        </span>
                                        <span style={{
                                            fontSize: '11px', color: '#9ca3af',
                                            backgroundColor: '#f3f4f6',
                                            padding: '2px 8px', borderRadius: '999px',
                                        }}>
                                            {filteredLeads.length} leads
                                        </span>

                                        {/* Estágios resumidos */}
                                        <div style={{ display: 'flex', gap: '6px', marginLeft: '8px', flexWrap: 'wrap' }}>
                                            {(pipelineAtiva.estagios || []).map(estagio => {
                                                const count = filteredLeads.filter(l => l.estagio_id === estagio.id).length;
                                                return (
                                                    <div key={estagio.id} style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                        padding: '2px 8px', borderRadius: '999px',
                                                        backgroundColor: `${estagio.cor}15`,
                                                        border: `1px solid ${estagio.cor}30`,
                                                        fontSize: '11px', color: estagio.cor, fontWeight: '500',
                                                    }}>
                                                        <span>{estagio.nome}</span>
                                                        {count > 0 && (
                                                            <span style={{
                                                                backgroundColor: estagio.cor,
                                                                color: '#fff',
                                                                borderRadius: '999px',
                                                                padding: '0 5px',
                                                                fontSize: '10px',
                                                                fontWeight: '700',
                                                            }}>
                                                                {count}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Kanban */}
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Container fluid style={{ padding: '20px 24px' }}>
                                            <div className="kanban-board">
                                                {(pipelineAtiva.estagios || []).map(estagio => {
                                                    const estagioLeads = filteredLeads.filter(l => l.estagio_id === estagio.id);
                                                    return (
                                                        <MarketingKanbanColumn
                                                            key={estagio.id}
                                                            column={{
                                                                id: String(estagio.id),
                                                                title: estagio.nome,
                                                                color: estagio.cor,
                                                            }}
                                                            leads={estagioLeads}
                                                            onCardClick={handleCardClick}
                                                            onWhatsAppClick={handleWhatsAppClick}
                                                            isDropDisabled={estagio.tipo === 'convertido'}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </Container>
                                    </DragDropContext>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Modais ── */}
            <LeadModal
                show={showLeadModal}
                onHide={() => setShowLeadModal(false)}
                lead={selectedLead}
                colaboradores={colaboradoresMarketing}
                currentUser={currentUser}
                isSaving={isSavingLead}
                showAlertModal={showAlertModal}
                onSave={handleSaveLead}
                onWhatsAppClick={handleWhatsAppClick}
                onOpenScheduleModal={(paciente) => {
                    setPacienteParaAgendar(paciente);
                    setShowAgendamentoModal(true);
                }}
            />

            {pacienteParaAgendar && (
                <AgendamentoLeadModal
                    show={showAgendamentoModal}
                    onHide={() => setShowAgendamentoModal(false)}
                    paciente={pacienteParaAgendar}
                    lead={selectedLead}
                    especialidades={supportData.especialidades || []}
                    todosMedicos={supportData.todosMedicos || []}
                    servicos={supportData.servicos || []}
                    agendaConfig={agendaConfig}
                    onLeadUpdated={(updatedLead) => {
                        setLeads(prev => prev.map(l => l.id === updatedLead.id ? updatedLead : l));
                    }}
                />
            )}

            {showWhatsAppChat && whatsAppLead && (
                <WhatsAppChat
                    lead={whatsAppLead}
                    pipelineConnections={pipelineAtiva?.connections || []}
                    onClose={() => { setShowWhatsAppChat(false); setWhatsAppLead(null); }}
                    onMensagemEnviada={(leadAtualizado) => {
                        setLeads(prev => prev.map(l => l.id === leadAtualizado.id ? leadAtualizado : l));
                    }}
                />
            )}

            <PipelinesConfig
                show={showPipelinesConfig}
                onClose={() => {
                    setShowPipelinesConfig(false);
                    fetchPipelines();
                }}
            />
        </>
    );
};

export default MarketingPage;
